import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../api/axios";
import Cookies from "js-cookie";

const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:8080/ERP/rest";

const initialState = {
  user: JSON.parse(localStorage.getItem("user")) || null,
  loading: false,
  error: null,
};

// Async thunk for login
export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async ({ username, password }, { rejectWithValue }) => {
    try {
      const payload = {
        username: username,
        password: btoa(password), // base64 encode
        loginType: "regular",
      };
      
      const response = await axios.post(
        `${BASE_URL}/login/doLogin`,
        payload,
        { headers: { "Content-Type": "application/json" } }
      );
      
      const data = response.data;

      if (data && (data.status === "1" || data.status === 1)) {
        const user = data.userdetails && data.userdetails[0] ? data.userdetails[0] : data.user || data;
        const token = user.token || user.accessToken || user.authToken;

        if (!token) {
          return rejectWithValue("Authentication token not received from server.");
        }

        // --- KEY CHANGES ---
        // Set all necessary items for session persistence and route protection
        localStorage.setItem("authToken", token); // For ProtectedRoute
        localStorage.setItem("user", JSON.stringify(user)); // For user details
        Cookies.set("token", token, { expires: 7 }); // For API requests

        return user;
      } else {
        return rejectWithValue(data.message || "Invalid username or password.");
      }
    } catch (error) {
      const errorMsg = error.response?.data?.message || "An error occurred during login.";
      return rejectWithValue(errorMsg);
    }
  }
);

// Async thunk for logout
export const logoutUser = createAsyncThunk(
  "auth/logoutUser",
  async () => {
    // --- KEY CHANGES ---
    // Clear all session-related storage
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    Cookies.remove("token");
    return null;
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    // This can be used on app initialization to sync state with storage
    restoreUser: (state) => {
      const user = localStorage.getItem("user");
      const token = Cookies.get("token");
      if (user && token) {
        state.user = JSON.parse(user);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Login cases
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.error = null;
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.user = null;
      })
      // Logout cases
      .addCase(logoutUser.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.error = null;
      });
  },
});

export const { clearError, restoreUser } = authSlice.actions;

export default authSlice.reducer;